import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { PublicLayout } from '../components/layout/PublicLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Bus, MapPin, Clock, ArrowRight, User, AlertCircle, Calendar, CheckCircle2 } from 'lucide-react';
import { format, parseISO, isToday, isTomorrow } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SeatSelectionPage = () => {
  const { tripId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [trip, setTrip] = useState(null);
  const [seats, setSeats] = useState([]);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seatLayout, setSeatLayout] = useState('3-2');
  
  const passengers = parseInt(searchParams.get('passengers') || '1');
  const currency = searchParams.get('currency') || 'USD';

  useEffect(() => {
    fetchTripData();
  }, [tripId]);

  const fetchTripData = async () => {
    setLoading(true);
    try {
      const [tripRes, seatsRes] = await Promise.all([
        axios.get(`${API_URL}/trips/${tripId}`),
        axios.get(`${API_URL}/trips/${tripId}/seats`)
      ]);
      setTrip(tripRes.data);
      setSeats(seatsRes.data.seats);
      setSeatLayout(seatsRes.data.layout || '2-2');
    } catch (error) {
      console.error('Failed to fetch trip:', error);
      toast.error('Failed to load trip details');
    } finally {
      setLoading(false);
    }
  };

  const toggleSeat = (seatNumber) => {
    const seat = seats.find(s => s.number === seatNumber);
    if (seat.status === 'booked') return;

    if (selectedSeats.includes(seatNumber)) {
      setSelectedSeats(selectedSeats.filter(s => s !== seatNumber));
    } else if (selectedSeats.length < passengers) {
      setSelectedSeats([...selectedSeats, seatNumber]);
    } else {
      toast.warning(`You can only select ${passengers} seat(s)`);
    }
  };

  const getSeatClass = (seat) => {
    if (seat.status === 'booked') return 'seat seat-booked';
    if (selectedSeats.includes(seat.number)) return 'seat seat-selected';
    return 'seat seat-available';
  };

  const handleContinue = () => {
    if (selectedSeats.length !== passengers) {
      toast.error(`Please select ${passengers} seat(s)`);
      return;
    }
    
    const params = new URLSearchParams({
      seats: selectedSeats.join(','),
      currency
    });
    navigate(`/booking/${tripId}?${params.toString()}`);
  };

  const price = trip ? (currency === 'USD' ? trip.price_usd : trip.price_zar) : 0;
  const totalPrice = price * selectedSeats.length;
  const currencySymbol = currency === 'USD' ? '$' : 'R';

  // Group seats by row for display
  const seatsByRow = seats.reduce((acc, seat) => {
    if (!acc[seat.row]) acc[seat.row] = [];
    acc[seat.row].push(seat);
    return acc;
  }, {});

  if (loading) {
    return (
      <PublicLayout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Skeleton className="h-40 w-full mb-6" />
          <Skeleton className="h-96 w-full" />
        </div>
      </PublicLayout>
    );
  }

  if (!trip) {
    return (
      <PublicLayout>
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <AlertCircle className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold mb-2">Trip Not Found</h2>
          <Button onClick={() => navigate('/')} className="btn-panashe">
            Search Again
          </Button>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Trip Summary */}
        <Card className="card-panashe mb-6 overflow-hidden border-2 border-slate-200">
          {/* Top Date Banner */}
          {trip && (() => {
            const depDate = trip.departure_datetime ? parseISO(trip.departure_datetime) : null;
            const depToday = depDate ? isToday(depDate) : false;
            const depTomorrow = depDate ? isTomorrow(depDate) : false;

            return (
              <div className={`px-6 py-2.5 flex flex-wrap items-center justify-between gap-2 border-b ${
                depToday 
                  ? 'bg-emerald-600 text-white' 
                  : depTomorrow 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-slate-800 text-white'
              }`}>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span className="text-xs uppercase font-bold tracking-wider opacity-90">Departure Date:</span>
                  <span className="font-extrabold text-sm sm:text-base underline decoration-white/40">
                    {depDate ? format(depDate, 'EEEE, dd MMMM yyyy') : ''}
                  </span>
                  {depToday && (
                    <span className="bg-white text-emerald-800 text-xs px-2.5 py-0.5 rounded-full font-black uppercase tracking-wider">
                      ● TODAY'S TRIP
                    </span>
                  )}
                  {depTomorrow && (
                    <span className="bg-white text-blue-800 text-xs px-2.5 py-0.5 rounded-full font-black uppercase tracking-wider">
                      ★ TOMORROW'S TRIP
                    </span>
                  )}
                </div>
                <div className="text-xs font-semibold opacity-90">
                  Bus: <span className="font-bold">{trip.bus_registration || 'Panashe Coach'}</span>
                </div>
              </div>
            );
          })()}

          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-panashe-blue rounded-xl flex items-center justify-center shadow">
                  <Bus className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h1 className="font-heading text-xl sm:text-2xl font-bold text-slate-900">
                    {trip.origin} → {trip.destination}
                  </h1>
                  <p className="text-sm text-slate-600 mt-0.5">{trip.route_name}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-6 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <div className="text-center">
                  <div className="text-xs uppercase font-bold text-panashe-blue mb-0.5">Departs</div>
                  <p className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900 leading-none">
                    {format(parseISO(trip.departure_datetime), 'HH:mm')}
                  </p>
                  <p className="text-xs font-bold text-panashe-blue mt-1">
                    {format(parseISO(trip.departure_datetime), 'EEE, dd MMM yyyy')}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">{trip.origin}</p>
                </div>

                <div className="text-slate-400 font-bold px-1">➔</div>

                <div className="text-center">
                  <div className="text-xs uppercase font-bold text-emerald-700 mb-0.5">Arrives</div>
                  <p className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900 leading-none">
                    {format(parseISO(trip.arrival_datetime), 'HH:mm')}
                  </p>
                  <p className="text-xs font-bold text-emerald-700 mt-1">
                    {format(parseISO(trip.arrival_datetime), 'EEE, dd MMM yyyy')}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">{trip.destination}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Seat Map */}
          <div className="lg:col-span-2">
            <Card className="card-panashe">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Select Your Seats
                </CardTitle>
                <p className="text-sm text-slate-600">
                  Select {passengers} seat{passengers > 1 ? 's' : ''} • {selectedSeats.length}/{passengers} selected
                </p>
              </CardHeader>
              <CardContent>
                {/* Legend */}
                <div className="flex items-center gap-4 mb-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="seat seat-available w-6 h-6 text-[10px]">1</div>
                    <span className="text-slate-600">Available</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="seat seat-selected w-6 h-6 text-[10px]">2</div>
                    <span className="text-slate-600">Selected</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="seat seat-booked w-6 h-6 text-[10px]">3</div>
                    <span className="text-slate-600">Booked</span>
                  </div>
                </div>

                {/* Bus Layout */}
                <div className="bg-slate-100 rounded-xl p-6">
                  {/* Driver Area */}
                  <div className="flex justify-between items-center mb-6 pb-4 border-b-2 border-dashed border-slate-300">
                    <div className="text-sm text-slate-500 font-medium">DRIVER</div>
                    <div className="text-sm text-slate-500 font-medium text-panashe-blue">FRONT (3 - 2 SEATING)</div>
                  </div>

                  {/* Seats Grid */}
                  <div className="space-y-2">
                    {Object.entries(seatsByRow).map(([row, rowSeats]) => {
                      const leftSeats = rowSeats
                        .filter(s => s.side === 'left' || (!s.side && Number(s.column) <= 3))
                        .sort((a, b) => parseInt(a.number, 10) - parseInt(b.number, 10));
                      const rightSeats = rowSeats
                        .filter(s => s.side === 'right' || (!s.side && Number(s.column) > 3))
                        .sort((a, b) => parseInt(a.number, 10) - parseInt(b.number, 10));

                      return (
                        <div key={row} className="flex items-center gap-2" data-testid={`seat-row-${row}`}>
                          <span className="w-6 text-xs text-slate-400 text-right font-mono">R{row}</span>
                          <div className="flex-1 flex justify-center gap-3">
                            {/* Left side seats (3 seats in 3-2 layout) */}
                            <div className="flex gap-1.5">
                              {leftSeats.map(seat => (
                                <button
                                  key={seat.number}
                                  className={getSeatClass(seat)}
                                  onClick={() => toggleSeat(seat.number)}
                                  disabled={seat.status === 'booked'}
                                  data-testid={`seat-${seat.number}`}
                                  title={`Seat ${seat.number}`}
                                >
                                  {seat.number}
                                </button>
                              ))}
                            </div>
                            
                            {/* Aisle */}
                            <div className="w-6 sm:w-10 flex items-center justify-center">
                              <div className="h-full border-r border-dashed border-slate-300"></div>
                            </div>
                            
                            {/* Right side seats (2 seats in 3-2 layout) */}
                            <div className="flex gap-1.5">
                              {rightSeats.map(seat => (
                                <button
                                  key={seat.number}
                                  className={getSeatClass(seat)}
                                  onClick={() => toggleSeat(seat.number)}
                                  disabled={seat.status === 'booked'}
                                  data-testid={`seat-${seat.number}`}
                                  title={`Seat ${seat.number}`}
                                >
                                  {seat.number}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Rear */}
                  <div className="mt-6 pt-4 border-t-2 border-dashed border-slate-300 text-center text-sm text-slate-500">
                    REAR / TOILET
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Booking Summary */}
          <div>
            <Card className="card-panashe sticky top-24">
              <CardHeader className="bg-panashe-blue text-white rounded-t-lg">
                <CardTitle className="font-heading">Booking Summary</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Bus</span>
                    <span className="font-medium">{trip.bus_registration}</span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Passengers</span>
                    <span className="font-medium">{passengers}</span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Selected Seats</span>
                    <span className="font-medium">
                      {selectedSeats.length > 0 ? selectedSeats.join(', ') : 'None'}
                    </span>
                  </div>

                  <div className="border-t border-slate-200 pt-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-600">Price per seat</span>
                      <span>{currencySymbol}{price}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total</span>
                      <span className="text-panashe-blue">{currencySymbol}{totalPrice}</span>
                    </div>
                  </div>

                  <Button 
                    className="w-full btn-panashe"
                    onClick={handleContinue}
                    disabled={selectedSeats.length !== passengers}
                    data-testid="continue-booking-btn"
                  >
                    Continue to Booking
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default SeatSelectionPage;
